import { Persona } from "@/api/personas.ts";
import { X } from "@/components/logos/X.tsx";
import { Button } from "@/components/ui/button.tsx";
import { clsx } from "clsx";
import { CAPABILITY_CARD_COLORS, CapabilityCard } from "./CapabilityCard.tsx";
import { Product, productQueries } from "@/api/product.ts";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  Automation,
  AutomationCreate,
  automationQueries,
  createAutomation,
  persistAutomationStatus,
} from "@/api/automations.ts";
import { useToast } from "@/hooks/use-toast.ts";
import { LoaderCircle, SettingsIcon } from "lucide-react";
import { Role, useAuthContext } from "@/components/AuthorizationProvider.tsx";
import { initiateTwitterLogin, initiateTwitterLogout } from "@/api/twitter.ts";
import { Suspense } from "react";
import { CapabilityConfig } from "@/components/manage-personas/capabilities/CapabilityConfig.tsx";
import { CapabilitiesConfigModal } from "@/components/manage-personas/capabilities/CapabilitiesConfigModal.tsx";
import { useAuth } from "@/hooks/use-auth.tsx";

export function Capabilities({ persona }: { persona: Persona }) {
  const { hasAtLeastRole } = useAuth();
  const isConnectedToTwitter = !!persona.twitter;
  const isAdmin = hasAtLeastRole(Role.TenantAdmin);

  const editPersonaDisabled =
    !persona.persona_id || (persona.public_persona === true && !isAdmin);

  return (
    <section className="flex flex-col max-w-5xl">
      <p className="text-muted-foreground">
        These functions help your agent operate efficiently and effectively.
        Enable the features that best align with your goals and let your agent
        handle the rest—saving you time while maximizing engagement and impact.
      </p>

      {isConnectedToTwitter ? (
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-64">
              <LoaderCircle className="h-12 w-12 animate-spin text-primary" />
            </div>
          }
        >
          <div className="flex flex-row gap-x-4 items-end flex-wrap">
            <TwitterStatus className="mb-4" persona={persona} />
            {!editPersonaDisabled && (
              <CapabilitiesConfigModal personaId={persona.persona_id}>
                <Button className="mb-12 gap-x-2" variant="outline">
                  <SettingsIcon className="w-4" /> Configuration
                </Button>
              </CapabilitiesConfigModal>
            )}
          </div>
          <CapabilitiesGrid persona={persona} />
        </Suspense>
      ) : (
        <ConnectToTwitter
          className="my-8 self-center"
          personaId={persona.persona_id}
        />
      )}
    </section>
  );
}

function selectAutomationProducts(products: Product[]): Product[] {
  return products.filter((p) => p.automation_product === true);
}

function createAutomationMapBySku(automations: Automation[]) {
  return automations.reduce(
    (acc, automation) => {
      acc[automation.product_sku] = automation;
      return acc;
    },
    {} as Record<string, Automation>,
  );
}

function CapabilitiesGrid({ persona }: { persona: Persona }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentUser } = useAuthContext();
  const isOwner = persona.persona_owner === currentUser?.id;
  const { data: generalConfig } = useSuspenseQuery(
    automationQueries.generalConfig(persona.persona_id),
  );
  const canEditCapabilities = isOwner && !!generalConfig;

  const { data: automationProducts = [] } = useSuspenseQuery({
    ...productQueries.products(),
    select: selectAutomationProducts,
  });
  const { data: automations = {} } = useSuspenseQuery({
    ...automationQueries.automations(persona.persona_id),
    select: createAutomationMapBySku,
  });

  const setCapabilityStatusMutation = useMutation({
    mutationFn: ({
      automationId,
      enabled,
    }: {
      automationId: string;
      enabled: boolean;
    }) => persistAutomationStatus(persona.persona_id, automationId, enabled),
    onSuccess: (_, { enabled }) => {
      queryClient.invalidateQueries(
        automationQueries.automations(persona.persona_id),
      );

      toast({
        title: enabled ? "Enabled" : "Disabled",
        description: "Automation toggled successfully",
      });
    },
    onError: (error: any) => {
      console.error(error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.detail || "Failed to toggle automation",
        variant: "destructive",
      });
    },
  });
  const createCapabilityMutation = useMutation({
    mutationFn: ({ product }: { product: Product }) => {
      const payload: AutomationCreate = {
        automation_type: product.product_sku,
        product_sku: product.product_sku,
        name: product.name,
        description: product.description,
        metadata: { frequency: "daily", frequency_count: 1 },
        enabled: true,
      };

      return createAutomation(persona.persona_id, payload);
    },
    onSuccess: (_, { product }) => {
      queryClient.invalidateQueries(
        automationQueries.automations(persona.persona_id),
      );
      queryClient.invalidateQueries(
        automationQueries.costs(persona.persona_id),
      );

      toast({ title: "Automation Enabled", description: product.name });
    },
    onError: (error) => {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to create automation doc",
        variant: "destructive",
      });
    },
  });
  const mutating =
    setCapabilityStatusMutation.isPending || createCapabilityMutation.isPending;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {automationProducts.map((product, index) => {
        const automation = automations[product.product_sku];
        const automationEnabled = automation?.enabled ?? false;

        if (!isOwner && !automationEnabled) return null;

        return (
          <CapabilityCard
            key={index}
            title={product.name}
            description={product.description}
            cost={isOwner ? product.credit_cost : undefined}
            status={automationEnabled}
            canEdit={canEditCapabilities}
            disabled={mutating}
            onToggle={
              canEditCapabilities
                ? (enabled) =>
                    automation
                      ? setCapabilityStatusMutation.mutate({
                          automationId: automation.automation_id,
                          enabled,
                        })
                      : createCapabilityMutation.mutate({ product })
                : undefined
            }
            style={{
              // @ts-expect-error Although types don't include it, it's possible to set a CSS variable through the style prop
              "--color":
                CAPABILITY_CARD_COLORS[index % CAPABILITY_CARD_COLORS.length],
            }}
          >
            {canEditCapabilities && automation?.enabled ? (
              <CapabilityConfig
                className="mt-3 border-t border-gray-[#717171]"
                personaId={persona.persona_id}
                automation={automation}
                product={product}
              />
            ) : null}
          </CapabilityCard>
        );
      })}
    </div>
  );
}

function TwitterStatus({
  className,
  persona,
}: {
  className?: string;
  persona: Persona;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { hasAtLeastRole } = useAuth();

  const isAdmin = hasAtLeastRole(Role.TenantAdmin);

  const editPersonaDisabled =
    !persona.persona_id || (persona.public_persona === true && !isAdmin);

  const logoutFromTwitter = async () => {
    try {
      await initiateTwitterLogout(persona.persona_id);
      toast({ description: "Your X account has been disconnected." });
      await queryClient.invalidateQueries({ queryKey: ["personas"] });
    } catch (error) {
      console.error("Error initiating Twitter logout:", error);
      toast({
        description: "Error connecting to Twitter. Please try again later.",
      });
    }
  };

  return (
    <div className={clsx("", className)}>
      <div className="py-4">
        <h6 className="font-medium text-muted-foreground mb-2 uppercase">
          Twitter status
        </h6>
        <div className="flex flex-wrap w-fit justify-between items-center rounded-lg border p-4 gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 flex-shrink-0 rounded-full bg-green-500"
              style={{ boxShadow: "0px 1px 13px 2px #0DFE00" }}
            />
            <span className="font-medium">
              Connected as{" "}
              <span className="font-semibold">@{persona.twitter}</span>
            </span>
          </div>
          {!editPersonaDisabled && (
            <Button onClick={logoutFromTwitter}>Logout</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ConnectToTwitter({
  className,
  personaId,
}: {
  className?: string;
  personaId: string;
}) {
  const { toast } = useToast();
  const handleConnectTwitter = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    try {
      e.preventDefault();
      const redirectUrl = encodeURIComponent(window.location.href);
      window.location.href = await initiateTwitterLogin(personaId, redirectUrl);
    } catch (error) {
      console.error("Error initiating Twitter login:", error);
      toast({
        description: "Error connecting to Twitter. Please try again later.",
      });
    }
  };
  return (
    <div
      className={clsx(
        "flex flex-col max-w-md items-center text-center gap-2",
        className,
      )}
    >
      <div className="bg-black p-1">
        <X fill="white" />
      </div>
      <h6>Twitter connection required</h6>
      <p className="text-sm font-normal">
        Connect your Twitter account to enable capabilities and autonomous agent
        interactions
      </p>
      <Button className="mt-2" onClick={handleConnectTwitter}>
        Connect Twitter
      </Button>
    </div>
  );
}
